import * as ko from 'knockout';
import {IOverride, ISyncField, ISystem, ISystemPermission, ISystemRole} from '../../../index';
import {ObjectTypeId, SYSTEM_MEGA_EADM} from '../../../constants';
import {getObjectType, getPermissionDisplayName} from '../../../util/misc';
import {IComboboxV2Options} from '../../combobox/v2';
import {IComboboxOptions_Department, TObjectData_basicDepartment} from '../../combobox/department';
import {lm} from '../../../appContext';
import {nsTooltip} from '../../../util/nsTooltip';
import {storeObservable} from '../../../util/sessionstore';
import {KOSubscriptionTracker} from '../../../util/KOSubscriptionTracker';
import {nsOverride} from '../../override/nsOverride';
import {PERMISSIONS_OVERRIDE_DETAILS_KEYS} from '../../override/overrideDetailsKeys';
import {nsUserPermissions} from './nsUserPermissions';
import {nsUserPermissionPolicy} from './nsUserPermissionPolicy';
import {PermissionOverrideDetails} from './PermissionOverrideDetails';
import isOverrideEffectivelyRemoved = nsOverride.isOverrideEffectivelyRemoved;
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;
import OverrideValue = nsOverride.OverrideValue;
import IOrgPolicyContext = nsUserPermissionPolicy.IOrgPolicyContext;
import IRowCapabilities = nsUserPermissionPolicy.IRowCapabilities;
import IPermissionIdentity = nsUserPermissionPolicy.IPermissionIdentity;

/** CSS targets inside the summary row that must not toggle expand (comboboxes, date, actions, checkbox). */
const EXPAND_CLICK_IGNORE =
    'input, textarea, select, a, .combobox-v2, .comp-combobox-specific, .permissions-v2-grid__actions, .permissions-v2-grid__checkbox';

/** Shared empty list so disabled department pickers don't allocate a new `[]` every recompute. */
const EMPTY_DEPARTMENTS: TObjectData_basicDepartment[] = [];

/** Page-level actions PermissionRow delegates to (edit exclusivity + mutations). */
export interface IPermissionRowActions {
    /** False while any delete/save/restore request is in flight. */
    canMutateRows: KnockoutObservable<boolean> | KnockoutComputed<boolean>;
    /** True while any org has draft New Permission rows (page-scoped add mode). */
    isAddMode: KnockoutObservable<boolean> | KnockoutComputed<boolean>;
    /** True while bulk-action-bar intermediate edit is active (multi-row edit). */
    isBulkEditing: KnockoutObservable<boolean> | KnockoutComputed<boolean>;
    /** True when no add / row-edit / bulk-edit is active (object links, etc.). */
    isReadMode: KnockoutObservable<boolean> | KnockoutComputed<boolean>;
    /** The single row currently in edit mode, if any (not used during bulk edit). */
    editingRow: KnockoutObservable<PermissionRow | null>;
    beginEdit: (row: PermissionRow) => void;
    cancelEdit: (row: PermissionRow) => void;
    saveEdit: (row: PermissionRow) => void;
    requestDelete: (row: PermissionRow) => void;
    requestRestore: (row: PermissionRow) => void;
    /** Other rows in the same org (for save validation). */
    getSiblingIdentities: (row: PermissionRow) => IPermissionIdentity[];
    /** Occupied system/role/dept slots for combobox filtering (excludes this row). */
    getOccupancySlots: (row: PermissionRow) => nsUserPermissionPolicy.IOccupancySlot[];
    /** Copy this row into a New Permission draft (same org). */
    requestCopy: (row: PermissionRow) => void;
    /** Persist only the permission note (inline card edit). */
    savePermissionComment: (row: PermissionRow) => void;
    /** Altinn harvest: mark grant Keep (K) so leaver revoke skips it. */
    requestRetain: (row: PermissionRow) => void;
    /** Clear an active Altinn retain (K) override. */
    requestClearRetain: (row: PermissionRow) => void;
}

/**
 * One permission row: read display, inline edit fields, and policy-gated actions.
 * Expanded override UI lives on {@link overrideDetails}.
 */
export class PermissionRow {
    systemPermission: ISystemPermission;
    readonly system: ISystem | null;
    readonly isManual: boolean;
    /** Collapsed AP+active-R + MP pair — single-row undo deletes MP then paired R (restore confirm). */
    readonly isModifiedPair: KnockoutObservable<boolean>;
    readonly role: KnockoutObservable<ISystemRole | null>;
    readonly department: KnockoutObservable<TObjectData_basicDepartment | null>;
    readonly expirydate: KnockoutObservable<string>;
    readonly needsDepartment: KnockoutComputed<boolean>;
    /** Show the scope cell (recursive department or LegalUnit / managerOf). */
    readonly showScope: KnockoutComputed<boolean>;
    readonly roleDisplayName: KnockoutComputed<string>;
    readonly roleTooltip: KnockoutComputed<string>;
    readonly departmentDisplayName: KnockoutComputed<string>;
    readonly departmentTooltip: KnockoutComputed<string>;
    readonly roleHref: KnockoutComputed<string | null>;
    /** Department or LegalUnit details; null when not read mode or not allowed. */
    readonly scopeHref: KnockoutComputed<string | null>;
    readonly hasPermissionComment: KnockoutComputed<boolean>;
    readonly permissionCommentText: KnockoutComputed<string>;
    /** Read-mode expiry string from the current SP (reactive after in-place reconcile). */
    readonly expirydateServerText: KnockoutComputed<string>;
    /** Draft for new-permission rows and inline note-card edit. */
    readonly commentDraft: KnockoutObservable<string> = ko.observable('');
    readonly isEditingPermissionComment = ko.observable(false);
    readonly isSavingPermissionComment = ko.observable(false);
    readonly canEditPermissionComment: KnockoutComputed<boolean>;
    /** True when the note-card draft differs from the saved permission comment. */
    readonly canSavePermissionComment: KnockoutComputed<boolean>;
    readonly isExpandable: KnockoutComputed<boolean>;
    /** Chevron / row-click expand: expandable, not editing, not locked by add/bulk/other edit. */
    readonly canToggleExpand: KnockoutComputed<boolean>;
    /**
     * Assigned column: resolved person name, or translated `By user` / `eAdm` fallback.
     * Re-translates fallbacks when the language changes.
     */
    readonly assignedByText: KnockoutComputed<string>;
    readonly comboboxParams: IComboboxV2Options<ISystemRole>;
    readonly departmentComboboxParams: IComboboxOptions_Department;
    readonly departmentOptions: KnockoutComputed<TObjectData_basicDepartment[]>;
    readonly isEditing: KnockoutObservable<boolean> = ko.observable(false);
    readonly canSave: KnockoutComputed<boolean>;
    readonly isEffectivelyRemoved: KnockoutComputed<boolean>;
    /** False for removed / pending soft-delete / definitionreadonly (no bulk checkbox). */
    readonly showSelectionCheckbox: KnockoutComputed<boolean>;
    readonly showRestoreActions: KnockoutComputed<boolean>;
    readonly showMutateActions: KnockoutComputed<boolean>;
    readonly showEditActions: KnockoutComputed<boolean>;
    /**
     * Altinn harvest rows on ReadOnly systems: retain / clear-retain (K override).
     * Hidden for normal mutate chrome; shown even when {@link isSystemDefinitionReadOnly}.
     */
    readonly showRetainActions: KnockoutComputed<boolean>;
    readonly hasActiveRetain: KnockoutComputed<boolean>;
    readonly canRetainAction: KnockoutComputed<boolean>;
    readonly canClearRetainAction: KnockoutComputed<boolean>;
    /** False while page isBusy — keeps edit chrome looking disabled under inert. */
    readonly canInteract: KnockoutComputed<boolean>;
    readonly canEditAction: KnockoutComputed<boolean>;
    readonly canDeleteAction: KnockoutComputed<boolean>;
    readonly canRestoreAction: KnockoutComputed<boolean>;
    readonly canCopyAction: KnockoutComputed<boolean>;
    /** Pending remove / pending soft-delete → cancel; synced active R → restore. */
    readonly restoreActionTooltipKey: KnockoutComputed<string>;
    readonly rowDenyTooltip: KnockoutComputed<string>;
    readonly isExpanded = ko.observable(false);
    /** Row selection for bulk actions within a system group. */
    readonly isSelected = ko.observable(false);
    /**
     * Existing row is inert in page add mode, or when another row is being edited.
     * The row currently in edit mode is never locked.
     */
    readonly isExistingRowLocked: KnockoutComputed<boolean>;
    /** Host for shared override-details component. */
    readonly overrideDetails: PermissionOverrideDetails;
    /** Keys bag for shared override-details (permissions-owned CMS keys). */
    readonly overrideDetailsKeys = PERMISSIONS_OVERRIDE_DETAILS_KEYS;

    private readonly departmentComboboxEnable: KnockoutComputed<boolean>;
    /** One occupancy snapshot per change, shared by this row's pickers and copy check. */
    private readonly occupancySlots: KnockoutComputed<nsUserPermissionPolicy.IOccupancySlot[]>;
    private readonly roleOptions: KnockoutComputed<ISystemRole[]>;
    /** Last emitted picker lists — reused when ids are unchanged so combobox `data` does not notify. */
    private cachedRoles: ISystemRole[] = [];
    private cachedDepartments: TObjectData_basicDepartment[] = EMPTY_DEPARTMENTS;
    /** Raw assignee name from evaluateexpression / override; null → use fallback key. */
    private readonly assignedByResolvedName = ko.observable<string | null>(null);
    /**
     * True after the first assignee resolve attempt for this row instance.
     * Distinguishes “not fetched yet” from “fetched, show fallback” (resolved name null).
     */
    private assigneePrefetchCompleted = false;
    private readonly assignedByFallbackKey: string;
    private ownOverride: IOverride | null;
    private pairedRemoveOverride: IOverride | null;
    private readonly departments: KnockoutObservableArray<TObjectData_basicDepartment>;
    private readonly actions: IPermissionRowActions;
    private orgPolicy: IOrgPolicyContext;
    private baseCapabilities: IRowCapabilities;
    /** Page-gated edit/delete/restore caps — shared by action enables and deny tooltip. */
    private readonly gatedCapabilities: KnockoutComputed<{
        edit: nsUserPermissionPolicy.ICapability
        delete: nsUserPermissionPolicy.ICapability
        restore: nsUserPermissionPolicy.ICapability
    }>;
    /**
     * Bumped when override fingerprints change in-place so KO action / removed
     * computeds re-read plain override fields and refreshed capabilities.
     */
    private readonly overrideRevision = ko.observable(0);
    /**
     * Bumped when {@link systemPermission} is replaced in-place (comment / expiry / etc.)
     * so read-mode computeds re-read plain SP fields without recreating the row.
     */
    private readonly spDataRevision = ko.observable(0);
    private readonly kosub = new KOSubscriptionTracker();
    private editSnapshot: {
        role: ISystemRole | null;
        department: TObjectData_basicDepartment | null;
        expirydate: string;
    } | null = null;

    constructor(
        systemPermission: ISystemPermission,
        system: ISystem | null,
        departments: KnockoutObservableArray<TObjectData_basicDepartment>,
        ownOverride: IOverride | null,
        actions: IPermissionRowActions,
        orgPolicy: IOrgPolicyContext,
        pairedRemoveOverride: IOverride | null = null,
    ) {
        this.systemPermission = systemPermission;
        this.system = system;
        this.ownOverride = ownOverride;
        this.pairedRemoveOverride = pairedRemoveOverride;
        this.isManual = !!systemPermission.manual;
        this.isModifiedPair = ko.observable(!!pairedRemoveOverride && this.isManual);
        this.departments = departments;
        this.actions = actions;
        this.orgPolicy = orgPolicy;
        this.overrideDetails = new PermissionOverrideDetails(
            () => this.getOverride(),
            () => this.getOverrideStatusParams()?.translationKey ?? null,
        );
        this.assignedByFallbackKey = this.isManual
            ? 'user.permissions.assigned.byuser'
            : 'user.permissions.assigned.eadm';
        if (!this.isManual) {
            this.assignedByResolvedName(
                nsUserPermissions.hasActiveRemoveOverride(ownOverride)
                    ? (ownOverride?.overriddenby?.trim() || null)
                    : null,
            );
        }
        this.assignedByText = ko.pureComputed(() => {
            storeObservable('language')();
            const resolved = this.assignedByResolvedName();
            if (resolved) {
                return resolved;
            }
            return lm.t(this.assignedByFallbackKey) ?? this.assignedByFallbackKey;
        });

        const initialDepartment = this.findDepartment(systemPermission.managerOf);
        this.baseCapabilities = nsUserPermissionPolicy.evaluateRow(orgPolicy, {
            systemPermission,
            searchableSystem: system,
            override: ownOverride,
            pairedRemoveOverride,
            isEffectivelyRemoved: isOverrideEffectivelyRemoved(ownOverride),
            departmentIsDeleted: !!(initialDepartment?.deleted),
        });

        this.role = ko.observable<ISystemRole | null>(this.resolveRole(systemPermission));
        this.department = ko.observable<TObjectData_basicDepartment | null>(initialDepartment);
        this.expirydate = ko.observable(
            nsUserPermissions.convertExpiryDateFromServerString(systemPermission.expirydate) ?? '',
        );

        this.roleDisplayName = ko.pureComputed(() => {
            storeObservable('language')();
            const role = this.role();
            return role ? this.getRoleDisplayName(role) : '';
        });
        this.roleTooltip = ko.pureComputed(() => {
            storeObservable('language')();
            const role = this.role();
            const system = this.system ?? this.systemPermission.system;
            return (system && role ? nsTooltip.getTitle_role(system, role) : null) ?? '';
        });
        this.departmentDisplayName = ko.pureComputed(() => {
            if (this.isLegalUnitScope) {
                return this.systemPermission.managerOfName || String(this.systemPermission.managerOf ?? '');
            }
            return this.department()?.name ?? this.systemPermission.managerOfName ?? '';
        });
        this.departmentTooltip = ko.pureComputed(() => {
            if (this.isLegalUnitScope) {
                return this.departmentDisplayName();
            }
            const dept = this.department();
            return (dept ? nsTooltip.getTitle_objectdata(dept) : null) ?? '';
        });
        this.roleHref = ko.pureComputed(() => {
            if (!this.actions.isReadMode()) {
                return null;
            }
            return nsUserPermissions.systemAccessHref(this.system ?? this.systemPermission.system);
        });
        this.scopeHref = ko.pureComputed(() => {
            if (!this.actions.isReadMode()) {
                return null;
            }
            if (this.isLegalUnitScope) {
                return nsUserPermissions.legalUnitScopeHref(this.systemPermission);
            }
            const departmentId = this.department()?.id ?? this.systemPermission.managerOf ?? 0;
            return nsUserPermissions.departmentDetailsHref(departmentId, this.systemPermission.companyid);
        });
        this.permissionCommentText = ko.pureComputed(() => {
            this.spDataRevision();
            return nsUserPermissions.permissionCommentText(this.systemPermission);
        });
        this.hasPermissionComment = ko.pureComputed(() => !!this.permissionCommentText());
        this.expirydateServerText = ko.pureComputed(() => {
            this.spDataRevision();
            return this.systemPermission.expirydate ?? '';
        });
        // MP always; AP only when an override is present (audit / note access).
        this.isExpandable = ko.pureComputed(() => this.isManual || this.hasOverride());
        this.needsDepartment = ko.pureComputed(() => {
            if (this.isLegalUnitScope) {
                return false;
            }
            return !!(this.role()?.recursive ?? this.systemPermission.recursive);
        });
        this.showScope = ko.pureComputed(() => {
            return this.isLegalUnitScope
                || this.needsDepartment()
                || !!this.systemPermission.managerOf;
        });
        this.occupancySlots = ko.pureComputed(() => this.actions.getOccupancySlots(this));
        this.roleOptions = ko.pureComputed(() => {
            const next = nsUserPermissionPolicy.rolesForPicker(
                this.system,
                this.occupancySlots(),
                this.role()?.id,
                this.systemPermission.role,
            );
            this.cachedRoles = nsUserPermissionPolicy.reuseArrayIfSameIds(this.cachedRoles, next);
            return this.cachedRoles;
        });
        this.departmentOptions = ko.pureComputed(() => {
            // Same gate as the template: no department field, no reason to carry
            // (and mount) thousands of unreachable options.
            if (!this.needsDepartment()) {
                this.cachedDepartments = EMPTY_DEPARTMENTS;
                return EMPTY_DEPARTMENTS;
            }
            const all = this.departments();
            const current = this.department();
            const withCurrent = current && !all.some(d => d.id == current.id) ? [current, ...all] : all;
            const system = this.system;
            const role = this.role();
            if (!system || !role?.recursive) {
                return withCurrent;
            }
            const occupied = this.occupancySlots();
            const next = nsUserPermissionPolicy.filterDepartmentsForPicker(
                withCurrent,
                system,
                role,
                occupied,
                current?.id,
            );
            this.cachedDepartments = nsUserPermissionPolicy.reuseArrayIfSameIds(
                this.cachedDepartments,
                next,
            );
            return this.cachedDepartments;
        });
        this.isEffectivelyRemoved = ko.pureComputed(() => {
            this.overrideRevision();
            return isOverrideEffectivelyRemoved(this.ownOverride);
        });
        this.showSelectionCheckbox = ko.pureComputed(() => {
            this.overrideRevision();
            return !this.isEffectivelyRemoved()
                && !isOverridePendingDeletion(this.ownOverride)
                && !this.isSystemDefinitionReadOnly;
        });

        const pageGates = () => {
            const isBusy = !this.actions.canMutateRows();
            // During bulk edit, non-participating rows count as "another row editing".
            const anotherRowEditing = this.actions.isBulkEditing()
                ? !this.isEditing()
                : this.actions.editingRow() != null && this.actions.editingRow() !== this;
            return {isBusy, anotherRowEditing};
        };

        this.gatedCapabilities = ko.pureComputed(() => {
            this.overrideRevision();
            const gates = pageGates();
            return {
                edit: nsUserPermissionPolicy.withPageGates(this.baseCapabilities.canEdit, gates),
                delete: nsUserPermissionPolicy.withPageGates(this.baseCapabilities.canDelete, gates),
                restore: nsUserPermissionPolicy.withPageGates(this.baseCapabilities.canRestore, gates),
            };
        });

        this.isExistingRowLocked = ko.pureComputed(() => {
            if (this.isEditing()) {
                return false;
            }
            if (this.actions.isAddMode()) {
                return true;
            }
            if (this.actions.isBulkEditing()) {
                // Selected participants stay unlocked so summary edit fields remain interactive.
                return !this.isSelected();
            }
            return this.actions.editingRow() != null && this.actions.editingRow() !== this;
        });
        this.canEditPermissionComment = ko.pureComputed(() => {
            return this.isManual
                && !this.isSystemDefinitionReadOnly
                && !this.isExistingRowLocked()
                && !this.isEditingPermissionComment()
                && this.actions.canMutateRows();
        });
        this.canSavePermissionComment = ko.pureComputed(() => {
            return this.commentDraft() !== this.permissionCommentText();
        });
        this.canToggleExpand = ko.pureComputed(() => {
            return this.isExpandable() && !this.isEditing() && !this.isExistingRowLocked();
        });

        this.canEditAction = ko.pureComputed(() => {
            return !this.isExistingRowLocked() && this.gatedCapabilities().edit.allowed;
        });
        this.canDeleteAction = ko.pureComputed(() => {
            return !this.isExistingRowLocked() && this.gatedCapabilities().delete.allowed;
        });
        this.canRestoreAction = ko.pureComputed(() => {
            this.overrideRevision();
            // Legacy soft-deleted K (pre hard-delete clear): cancel via restoreoverride.
            if (nsUserPermissions.isAltinnHarvestPermission(this.systemPermission)
                && isOverridePendingDeletion(this.ownOverride)
                && this.ownOverride?.value?.startsWith(OverrideValue.Retain)) {
                return !this.isExistingRowLocked()
                    && this.actions.canMutateRows()
                    && !!this.ownOverride?.id;
            }
            // Occupancy is explained on click (info dialog), not by disabling Restore.
            return !this.isExistingRowLocked()
                && this.gatedCapabilities().restore.allowed
                && !!this.ownOverride?.id;
        });
        this.canCopyAction = ko.pureComputed(() => {
            this.overrideRevision();
            if (this.isExistingRowLocked() || this.isEditing()) {
                return false;
            }
            // Removed / pending-delete rows deny edit; restore capability carries the same auth gates.
            const pendingDeletion = isOverridePendingDeletion(this.ownOverride);
            const copyAuth = this.isEffectivelyRemoved() || pendingDeletion
                ? this.gatedCapabilities().restore
                : this.gatedCapabilities().edit;
            if (!copyAuth.allowed) {
                return false;
            }
            const system = this.system;
            const role = this.role();
            if (!system || !role) {
                return false;
            }
            // occupancySlots() uses exceptRow (edit pickers); put this row back so Copy treats it as taken.
            const occupied: nsUserPermissionPolicy.IOccupancySlot[] = [...this.occupancySlots()];
            if (!this.isEffectivelyRemoved()) {
                occupied.push({
                    systemId: system.id,
                    roleId: role.id,
                    managerOf: this.department()?.id ?? this.systemPermission.managerOf ?? 0,
                });
            }
            return nsUserPermissionPolicy.hasAlternateCopySlot(
                system,
                occupied,
                this.departments(),
                role,
            );
        });
        this.showEditActions = ko.pureComputed(() => this.isEditing() && !this.actions.isBulkEditing());
        this.canInteract = ko.pureComputed(() => this.actions.canMutateRows());
        this.showRestoreActions = ko.pureComputed(() => {
            this.overrideRevision();
            if (this.isExistingRowLocked() || this.isEditing()) {
                return false;
            }
            // Legacy soft-deleted K (pre hard-delete clear) — cancel pending clear.
            if (nsUserPermissions.isAltinnHarvestPermission(this.systemPermission)
                && isOverridePendingDeletion(this.ownOverride)
                && this.ownOverride?.value?.startsWith(OverrideValue.Retain)) {
                return true;
            }
            if (this.isSystemDefinitionReadOnly) {
                return false;
            }
            return this.isEffectivelyRemoved()
                || isOverridePendingDeletion(this.ownOverride);
        });
        this.restoreActionTooltipKey = ko.pureComputed((): string => {
            this.overrideRevision();
            const override = this.ownOverride;
            // remove.pending or *.delete.pending — cancel the pending change; remove.synced — restore.
            const useCancel = isOverridePendingDeletion(override)
                || (nsUserPermissions.hasActiveRemoveOverride(override) && !override?.syncronizedimportfileid);
            return useCancel ? 'user.permissions.btn.cancel' : 'user.permissions.btn.restore';
        });
        this.showMutateActions = ko.pureComputed(() => {
            this.overrideRevision();
            return !this.isSystemDefinitionReadOnly
                && !this.isExistingRowLocked()
                && !this.isEditing()
                && !this.isEffectivelyRemoved()
                && !isOverridePendingDeletion(this.ownOverride);
        });
        this.hasActiveRetain = ko.pureComputed(() => {
            this.overrideRevision();
            return nsUserPermissions.hasActiveRetainOverride(this.ownOverride);
        });
        // Altinn harvest on ReadOnly systems: Keep / clear Keep (K) — not normal edit/delete chrome.
        this.showRetainActions = ko.pureComputed(() => {
            this.overrideRevision();
            return nsUserPermissions.isAltinnHarvestPermission(this.systemPermission)
                && !this.isExistingRowLocked()
                && !this.isEditing()
                && !this.isEffectivelyRemoved()
                && !isOverridePendingDeletion(this.ownOverride);
        });
        this.canRetainAction = ko.pureComputed(() => {
            return this.showRetainActions()
                && !this.hasActiveRetain()
                && this.actions.canMutateRows();
        });
        this.canClearRetainAction = ko.pureComputed(() => {
            return this.showRetainActions()
                && this.hasActiveRetain()
                && !!this.ownOverride?.id
                && this.actions.canMutateRows();
        });
        this.rowDenyTooltip = ko.pureComputed(() => {
            this.overrideRevision();
            // Integration-owned: no action chrome → no deny tooltip (same signal as hidden buttons).
            if (this.isSystemDefinitionReadOnly || this.isEditing() || this.isExistingRowLocked()) {
                return '';
            }
            const pendingDeletion = isOverridePendingDeletion(this.ownOverride);
            if (this.isEffectivelyRemoved() || pendingDeletion) {
                const cap = this.gatedCapabilities().restore;
                return cap.allowed ? '' : nsUserPermissionPolicy.denyTooltipKey(cap.reasons);
            }
            const edit = this.gatedCapabilities().edit;
            const del = this.gatedCapabilities().delete;
            if (edit.allowed && del.allowed) {
                return '';
            }
            return nsUserPermissionPolicy.denyTooltipKey(edit.reasons.length ? edit.reasons : del.reasons);
        });
        this.canSave = ko.pureComputed(() => {
            if (!this.canInteract() || !this.isDirty()) {
                return false;
            }
            if (!this.role() || (this.needsDepartment() && !this.department())) {
                return false;
            }
            return nsUserPermissionPolicy.validatePermissionDraft(
                this.toPolicyIdentity(),
                this.actions.getSiblingIdentities(this),
                this.system,
            ).allowed;
        });

        const editingInteractive = ko.pureComputed(() => this.isEditing() && this.canInteract());
        this.comboboxParams = {
            data: this.roleOptions,
            selected: this.role,
            enable: editingInteractive,
            lazyMountList: true,
            reloadWhen: storeObservable('language'),
            inputAttrs: {
                placeholder: ko.pureComputed(() => {
                    storeObservable('language')();
                    return lm.t('user.permissions.choose.permission') ?? '';
                }),
            },
            option: {
                label: (role: ISystemRole) => this.getRoleDisplayName(role),
                html: (role: ISystemRole) => nsUserPermissions.comboboxOptionHtmlWithDescription(
                    this.getRoleDisplayName(role),
                    role.description,
                ),
                key: (role: ISystemRole) => role.id,
                tooltip: (role: ISystemRole) => {
                    storeObservable('language')();
                    const system = this.system ?? this.systemPermission.system;
                    return system ? (lm.t(nsTooltip.getTitle_role(system, role) ?? '') ?? '') : '';
                },
                sortBy: [(role: ISystemRole) => {
                    if ((this.system ?? this.systemPermission.system)?.name == SYSTEM_MEGA_EADM) {
                        return role.roleenum;
                    }
                    return this.getRoleDisplayName(role).toLowerCase();
                }],
            },
            title: (role: ISystemRole | null) => {
                storeObservable('language')();
                const system = this.system ?? this.systemPermission.system;
                if (!role || !system) {
                    return '';
                }
                return lm.t(nsTooltip.getTitle_role(system, role) ?? '') ?? '';
            },
            inputStatus: (role: ISystemRole | null) => role ? 'ok' : 'warning',
        };

        this.departmentComboboxEnable = ko.pureComputed(() => {
            return this.isEditing() && this.needsDepartment() && this.canInteract();
        });
        this.departmentComboboxParams = {
            comboboxOptions: {
                data: this.departmentOptions,
                selected: this.department,
                enable: this.departmentComboboxEnable,
                // Department lists can be huge — keep the option DOM out until first open,
                // then render only the scrolled window.
                lazyMountList: true,
                virtualize: true,
                inputAttrs: {
                    placeholder: ko.pureComputed(() => {
                        storeObservable('language')();
                        return lm.t('user.permissions.choose.department') ?? '';
                    }),
                },
                option: {
                    key: (dept: TObjectData_basicDepartment) => dept.id,
                },
                inputStatus: (dept: TObjectData_basicDepartment | null) => {
                    return !this.needsDepartment() || !!dept ? 'ok' : 'warning';
                },
            },
        };

        this.kosub.subscribe(this.role, role => {
            if (!this.isEditing()) {
                return;
            }
            if (!role?.recursive) {
                this.department(null);
            } else if (!this.department()) {
                this.department(this.findDepartment(this.systemPermission.managerOf));
            }
        });

        this.kosub.subscribe(this.isExistingRowLocked, locked => {
            if (locked) {
                this.isExpanded(false);
            }
        });
    }

    toPolicyIdentity(opts?: {isEffectivelyRemoved?: boolean}): IPermissionIdentity {
        return nsUserPermissionPolicy.toIdentity(this.systemPermission, {
            roleId: this.role()?.id ?? 0,
            managerOf: this.department()?.id ?? this.systemPermission.managerOf ?? 0,
            isEffectivelyRemoved: opts?.isEffectivelyRemoved ?? this.isEffectivelyRemoved(),
        });
    }

    /** Occupancy / multiplerole* check as if this soft-removed AP were restored. */
    evaluateRestoreOccupancy(): nsUserPermissionPolicy.ICapability {
        return nsUserPermissionPolicy.validatePermissionDraft(
            this.toPolicyIdentity({isEffectivelyRemoved: false}),
            this.actions.getSiblingIdentities(this),
            this.system,
        );
    }

    hasOverride = ko.pureComputed(() => {
        this.overrideRevision();
        return !!(this.ownOverride || this.pairedRemoveOverride);
    });

    /** Display / expand: own override, or paired AP remove for Modified MP. */
    getOverride(): IOverride | null {
        return this.ownOverride ?? this.pairedRemoveOverride;
    }

    /** LegalUnit (Altinn) scope — display-only, never a department picker. */
    get isLegalUnitScope(): boolean {
        return nsUserPermissions.isLegalUnitScope(this.systemPermission);
    }

    /** Integration-owned system — no mutate/restore/select chrome; expand still allowed. */
    get isSystemDefinitionReadOnly(): boolean {
        return nsUserPermissions.isSystemDefinitionReadOnly(this.system ?? this.systemPermission.system);
    }

    /** Stored on this row only (never the paired AP remove). */
    getOwnOverride(): IOverride | null {
        return this.ownOverride;
    }

    /** Paired AP remove override when this MP is a Modified pair. */
    getPairedRemoveOverride(): IOverride | null {
        return this.pairedRemoveOverride;
    }

    /**
     * Whether policy allows Modified-pair undo (delete MP + deleteoverride), ignoring page edit locks.
     * Pass the paired `R` when the draft just became equal (row may not have pairedRemove yet).
     */
    canDeleteOverrideForAutoRestore(pairedOverride?: IOverride | null): boolean {
        const override = pairedOverride ?? this.pairedRemoveOverride;
        return this.baseCapabilities.canDelete.allowed
            && nsUserPermissionPolicy.canInteractWithAutomaticOverride(override);
    }

    /** Shape for page status-filter matching. */
    toStatusFilterRow(): nsUserPermissions.IPermissionStatusFilterRow {
        return {
            isManual: this.isManual,
            systemPermissionId: this.systemPermission.id ?? 0,
            ownOverride: this.ownOverride,
            pairedRemoveOverride: this.pairedRemoveOverride,
        };
    }

    getOverrideStatusParams() {
        if (this.isManual && this.pairedRemoveOverride) {
            return {
                ...nsUserPermissions.getModifiedPermissionOverrideDisplay(this.pairedRemoveOverride),
                hasComment: this.overrideDetails.hasComment,
            };
        }
        return this.ownOverride
            ? {
                ...nsUserPermissions.getPermissionOverrideDisplay(this.ownOverride),
                hasComment: this.overrideDetails.hasComment,
            }
            : null;
    }

    onRowClick = (_data: unknown, e: MouseEvent): boolean => {
        if (!this.canToggleExpand()) {
            return true;
        }
        if (e.target instanceof Element && e.target.closest(EXPAND_CLICK_IGNORE)) {
            return true;
        }
        this.isExpanded(!this.isExpanded());
        return true;
    };

    onEditClick = (): void => {
        if (this.canEditAction()) {
            this.actions.beginEdit(this);
        }
    };

    onCancelClick = (): void => {
        this.actions.cancelEdit(this);
    };

    onSaveClick = (): void => {
        if (this.canSave()) {
            this.actions.saveEdit(this);
        }
    };

    onDeleteClick = (): void => {
        if (this.canDeleteAction()) {
            this.actions.requestDelete(this);
        }
    };

    onCopyClick = (): void => {
        if (this.canCopyAction()) {
            this.actions.requestCopy(this);
        }
    };

    onRestoreClick = (): void => {
        if (this.canRestoreAction()) {
            this.actions.requestRestore(this);
        }
    };

    onRetainClick = (): void => {
        if (this.canRetainAction()) {
            this.actions.requestRetain(this);
        }
    };

    onClearRetainClick = (): void => {
        if (this.canClearRetainAction()) {
            this.actions.requestClearRetain(this);
        }
    };

    /** Stable element id for scroll-into-view after add/refresh. */
    getDomId(): string {
        return `permissions-v2-row-${permissionRowReconcileKey(this.systemPermission, this.getOwnOverride()).replace(/[^\w-]+/g, '-')}`;
    }

    startEditing(): void {
        this.editSnapshot = {
            role: this.role(),
            department: this.department(),
            expirydate: this.expirydate(),
        };
        // Edit uses summary cells only — never auto-open (or keep open) the details panel.
        this.isExpanded(false);
        this.isEditing(true);
    }

    /**
     * True when role / department / expiry differ from the snapshot taken at edit start.
     * Always reads the live observables first so KO dependents (e.g. bulk saveEnabled)
     * stay subscribed even when this runs before startEditing sets the snapshot.
     */
    isDirty(): boolean {
        const roleId = this.role()?.id ?? 0;
        const departmentId = this.department()?.id ?? 0;
        const expirydate = this.expirydate() ?? '';
        const snap = this.editSnapshot;
        if (!snap) {
            return false;
        }
        return roleId !== (snap.role?.id ?? 0)
            || departmentId !== (snap.department?.id ?? 0)
            || expirydate !== (snap.expirydate ?? '');
    }

    cancelEditing(): void {
        if (this.editSnapshot) {
            this.role(this.editSnapshot.role);
            this.department(this.editSnapshot.department);
            this.expirydate(this.editSnapshot.expirydate);
        }
        this.endEditing();
    }

    startEditPermissionComment = (): void => {
        if (!this.canEditPermissionComment()) {
            return;
        }
        this.commentDraft(this.permissionCommentText());
        this.isEditingPermissionComment(true);
    };

    cancelEditPermissionComment = (): void => {
        this.isEditingPermissionComment(false);
        this.commentDraft(this.permissionCommentText());
    };

    submitPermissionComment = (): void => {
        if (!this.isManual || this.isSavingPermissionComment() || !this.canSavePermissionComment()) {
            return;
        }
        this.actions.savePermissionComment(this);
    };

    finishEditing(): void {
        this.endEditing();
    }

    /** Leave edit mode; keep user expand preference unless the row is no longer expandable. */
    private endEditing(): void {
        this.editSnapshot = null;
        this.isEditing(false);
        if (!this.isExpandable()) {
            this.isExpanded(false);
        }
    }

    /** Sets a resolved assignee display name, or null to show the translated fallback key. */
    setAssignedByResolvedName(name: string | null): void {
        this.assignedByResolvedName(name?.trim() || null);
        this.assigneePrefetchCompleted = true;
    }

    /** SuperManager assignee prefetch — skip when this instance already resolved once. */
    needsAssigneePrefetch(): boolean {
        return this.isManual && !this.assigneePrefetchCompleted;
    }

    /**
     * True when this is the same stored SP (safe to reuse the row instance).
     * Role / dept / expiry / comment are mutable — {@link applyOverrideState} refreshes them.
     */
    matchesSpIdentity(sp: ISystemPermission): boolean {
        return this.systemPermission.id == sp.id
            && !!this.systemPermission.manual == !!sp.manual;
    }

    /**
     * Reuse this row after reconcile when SP identity still matches.
     * Skips capability re-eval / KO notify when override fingerprints are unchanged.
     * Always resyncs role/department/expiry from `sp` when not editing so a post-save
     * reuse (e.g. AP→MP after changing role) does not keep stale edit observables.
     */
    applyOverrideState(
        sp: ISystemPermission,
        ownOverride: IOverride | null,
        pairedRemoveOverride: IOverride | null,
        orgPolicy: IOrgPolicyContext,
    ): void {
        const fingerprintsUnchanged =
            sameOverrideFingerprint(this.ownOverride, ownOverride)
            && sameOverrideFingerprint(this.pairedRemoveOverride, pairedRemoveOverride);

        this.systemPermission = sp;
        this.ownOverride = ownOverride;
        this.pairedRemoveOverride = pairedRemoveOverride;
        this.spDataRevision(this.spDataRevision() + 1);

        const department = this.findDepartment(sp.managerOf);
        if (!this.isEditing()) {
            this.role(this.resolveRole(sp));
            this.department(department);
            this.expirydate(nsUserPermissions.convertExpiryDateFromServerString(sp.expirydate) ?? '');
        }

        if (fingerprintsUnchanged) {
            // Fingerprint skips comment and actor; still refresh the expanded panel.
            this.overrideDetails.bumpOverridePanelRevision();
            return;
        }

        if (!this.isManual) {
            this.assignedByResolvedName(
                nsUserPermissions.hasActiveRemoveOverride(ownOverride)
                    ? (ownOverride?.overriddenby?.trim() || null)
                    : null,
            );
        }
        this.orgPolicy = orgPolicy;
        this.isModifiedPair(!!pairedRemoveOverride && this.isManual);
        this.baseCapabilities = nsUserPermissionPolicy.evaluateRow(orgPolicy, {
            systemPermission: sp,
            searchableSystem: this.system,
            override: ownOverride,
            pairedRemoveOverride,
            isEffectivelyRemoved: isOverrideEffectivelyRemoved(ownOverride),
            departmentIsDeleted: !!(department?.deleted),
        });
        this.overrideRevision(this.overrideRevision() + 1);
        if (!this.showSelectionCheckbox()) {
            this.isSelected(false);
        }
        this.overrideDetails.bumpOverridePanelRevision();
    }

    dispose(): void {
        this.kosub.unsuscribeAll();
        this.overrideDetails.dispose();
        this.needsDepartment.dispose();
        this.departmentOptions.dispose();
        this.roleDisplayName.dispose();
        this.roleTooltip.dispose();
        this.departmentDisplayName.dispose();
        this.departmentTooltip.dispose();
        this.assignedByText.dispose();
        this.canSave.dispose();
        this.isEffectivelyRemoved.dispose();
        this.showSelectionCheckbox.dispose();
        this.showRestoreActions.dispose();
        this.showMutateActions.dispose();
        this.showRetainActions.dispose();
        this.hasActiveRetain.dispose();
        this.canRetainAction.dispose();
        this.canClearRetainAction.dispose();
        this.showEditActions.dispose();
        this.canInteract.dispose();
        this.gatedCapabilities.dispose();
        this.canEditAction.dispose();
        this.canDeleteAction.dispose();
        this.canRestoreAction.dispose();
        this.canCopyAction.dispose();
        this.restoreActionTooltipKey.dispose();
        this.rowDenyTooltip.dispose();
        this.isExistingRowLocked.dispose();
        this.canToggleExpand.dispose();
        this.departmentComboboxEnable.dispose();
        this.occupancySlots.dispose();
        this.roleOptions.dispose();
        this.hasOverride.dispose();
        this.isExpandable.dispose();
        this.roleHref.dispose();
        this.scopeHref.dispose();
        this.permissionCommentText.dispose();
        this.hasPermissionComment.dispose();
        this.canEditPermissionComment.dispose();
        this.canSavePermissionComment.dispose();
        this.expirydateServerText.dispose();
        this.showScope.dispose();
        // isModifiedPair / overrideRevision / spDataRevision are plain observables — no dispose.
    }

    /** Prefer searchable-system role object; fall back to the SP’s embedded role. */
    private resolveRole(sp: ISystemPermission): ISystemRole | null {
        const policyRoles = nsUserPermissionPolicy.availableRolesForSystem(this.system, sp.role);
        return policyRoles.find(r => r.id == sp.role?.id) ?? sp.role ?? null;
    }

    private findDepartment(managerOfId: number | null | undefined): TObjectData_basicDepartment | null {
        if (!managerOfId || nsUserPermissions.isLegalUnitScope(this.systemPermission)) {
            return null;
        }
        const found = this.departments().find(d => d.id == managerOfId);
        if (found) {
            return found;
        }
        const name = this.systemPermission.managerOfName || String(managerOfId);
        return {
            id: managerOfId,
            name,
            syncfields: [{name: 'Department', value: name}] as ISyncField[],
            type: getObjectType(ObjectTypeId.Department),
        };
    }

    private getRoleDisplayName(role: ISystemRole): string {
        // Prefer searchable system; fall back to the permission’s system so mega roles still translate.
        return getPermissionDisplayName(role, this.system ?? this.systemPermission.system ?? undefined)
            ?? role.name
            ?? '';
    }
}

/** Stable reconcile key: SP id when present, else override id, else system/role/dept/manual. */
export function permissionRowReconcileKey(
    sp: ISystemPermission,
    ownOverride: IOverride | null,
): string {
    if (sp.id) {
        return `sp:${sp.id}`;
    }
    if (ownOverride?.id) {
        return `ov:${ownOverride.id}`;
    }
    return `syn:${sp.system?.id ?? 0}:${sp.role?.id ?? 0}:${sp.managerOf ?? 0}:${sp.manual ? 1 : 0}`;
}

/** Compare override identity fields that drive removed / Modified UI. */
function sameOverrideFingerprint(a: IOverride | null, b: IOverride | null): boolean {
    return (a?.id ?? 0) == (b?.id ?? 0)
        && (a?.value ?? '') == (b?.value ?? '')
        && (a?.deletedbyid ?? 0) == (b?.deletedbyid ?? 0);
}
